import { useState } from 'react';
import { Box, Button, Card, CardContent, TextField, Typography, Alert, InputAdornment, Link } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BrandLogo } from '../components/BrandLogo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  // OTP step state
  const [otpToken, setOtpToken] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);

  const emailError = !email ? 'Email is required' : !EMAIL_RE.test(email) ? 'Enter a valid email address' : '';
  const passwordError = !password ? 'Password is required' : password.length < 8 ? 'Password must be at least 8 characters' : '';
  const credsValid = !emailError && !passwordError;

  const submitCreds = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!credsValid) return;
    login.mutate({ email, password }, {
      onSuccess: (data) => { setOtpToken(data.otpToken); setSentTo(data.email); setStep('otp'); setCode(''); },
    });
  };

  const submitOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return;
    verifyOtp.mutate({ otpToken, code }, { onSuccess: () => navigate('/') });
  };

  const onResend = () => {
    setResent(false);
    resendOtp.mutate(otpToken, { onSuccess: () => setResent(true) });
  };

  const backToLogin = () => { setStep('credentials'); setCode(''); verifyOtp.reset(); };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Card
        sx={{
          width: '100%',
          maxWidth: 400,
          overflow: 'visible',
          position: 'relative',
          background: 'linear-gradient(180deg, rgba(28,38,64,0.92), rgba(16,22,40,0.95))',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 30px 70px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: -2, borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(79,140,255,0.6), rgba(22,199,132,0.4))',
            filter: 'blur(26px)', opacity: 0.7, zIndex: -1,
          }}
        />
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <BrandLogo height={52} />
          </Box>

          {step === 'credentials' ? (
            <>
              <Typography variant="h5" sx={{ textAlign: 'center' }}>Welcome back</Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 0.5, mb: 3 }}>
                Sign in to your trading account
              </Typography>

              <form onSubmit={submitCreds}>
                <TextField
                  fullWidth label="Email" type="email" margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  error={touched.email && !!emailError}
                  helperText={touched.email ? emailError : ' '}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>) }}
                />
                <TextField
                  fullWidth label="Password" type="password" margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  error={touched.password && !!passwordError}
                  helperText={touched.password ? passwordError : ' '}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>) }}
                />
                {login.isError && <Alert severity="error" sx={{ mt: 2 }}>Invalid credentials</Alert>}
                <Button fullWidth size="large" variant="contained" type="submit" sx={{ mt: 3, py: 1.25 }} disabled={login.isPending}>
                  {login.isPending ? 'Sending code…' : 'Continue'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ textAlign: 'center' }}>Verify it's you</Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 0.5, mb: 3 }}>
                We emailed a 6-digit code to <b style={{ color: '#cdd6ea' }}>{sentTo}</b>. It expires in 5 minutes.
              </Typography>

              <form onSubmit={submitOtp}>
                <TextField
                  fullWidth label="6-digit code" margin="normal" autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.5em', fontWeight: 700 } }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><KeyOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>) }}
                />
                {verifyOtp.isError && <Alert severity="error" sx={{ mt: 2 }}>Invalid or expired code</Alert>}
                {resent && !verifyOtp.isError && <Alert severity="success" sx={{ mt: 2 }}>A new code was sent.</Alert>}
                <Button fullWidth size="large" variant="contained" type="submit" sx={{ mt: 3, py: 1.25 }} disabled={verifyOtp.isPending || code.length !== 6}>
                  {verifyOtp.isPending ? 'Verifying…' : 'Verify & Sign In'}
                </Button>
              </form>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Link component="button" type="button" underline="hover" onClick={backToLogin} sx={{ fontSize: 13 }}>
                  ← Back
                </Link>
                <Link component="button" type="button" underline="hover" onClick={onResend} sx={{ fontSize: 13, pointerEvents: resendOtp.isPending ? 'none' : 'auto', opacity: resendOtp.isPending ? 0.6 : 1 }}>
                  {resendOtp.isPending ? 'Resending…' : 'Resend code'}
                </Link>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
